import {
  PointerEvent as ReactPointerEvent,
  ReactNode,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link, NavLink } from 'react-router-dom'

import AccountMenu from '@/layouts/partials/AccountMenu'
import DropdownMenu from '@/layouts/partials/DropdownMenu'
import OrganizationButton, {
  useOrganizationName,
} from '@/layouts/partials/OrganizationButton'
import NavDrawer from '@/layouts/partials/NavDrawer'
import StatusIndicator from '@/layouts/partials/StatusIndicator'
import Tooltip from '@/components/Tooltip'
import { useMainNavItems } from '@/lib/hooks/useMainNavItems'
import {
  SIDEBAR_TOGGLE_ARIA_SHORTCUT,
  SIDEBAR_TOGGLE_SHORTCUT_LABEL,
  useSidebarCollapsed,
  useSidebarToggleShortcut,
} from '@/lib/hooks/useSidebarCollapsed'
import {
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  clampSidebarWidth,
  useSidebarWidth,
} from '@/lib/hooks/useSidebarWidth'
import { useOnClickOutside } from '@/lib/hooks/useOnClickOutside'
import { useAuth } from '@/lib/provider/AuthProvider'
import UserAvatar from '@/components/UserAvatar'
import { cn } from '@/lib/misc/Utils'

import ArrowTopRightIcon from '@icons/arrow-top-right-on-square'
import BookOpenIcon from '@icons/book-open'
import BuildingIcon from '@icons/building-office-2'
import ChevronUpDownIcon from '@icons/chevron-up-down'
import CodeBracketIcon from '@icons/code-bracket'
import CogIcon from '@icons/cog-6-tooth'
import CreditCardIcon from '@icons/credit-card'
import GlobeIcon from '@icons/globe-alt'
import HomeIcon from '@icons/home'
import LifebuoyIcon from '@icons/lifebuoy'
import LightBulbIcon from '@icons/light-bulb'
import MenuIcon from '@icons/bars-3'
import EllipsisIcon from '@icons/ellipsis-horizontal'
import XMarkIcon from '@icons/x-mark'

import PanelLeftIcon from '@fluent-icons/panel-left-24-regular'

const NAV_ICONS: Record<string, typeof HomeIcon> = {
  dashboard: HomeIcon,
  customers: BuildingIcon,
  insights: LightBulbIcon,
  billing: CreditCardIcon,
  tickets: LifebuoyIcon,
}

const RESOURCE_ICONS: Record<string, typeof HomeIcon> = {
  api: CodeBracketIcon,
  'knowledge-base': BookOpenIcon,
  map: GlobeIcon,
}

/** Shared row geometry so every sidebar row lines up exactly. */
const rowClass =
  'group flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-[14px] font-medium leading-5 transition-colors'
const rowIdleClass = 'text-neutral-70 hover:bg-neutral-20/70'
const rowActiveClass = 'bg-surface text-neutral-100 shadow-btn hover:bg-surface'
const iconClass = 'size-[18px] shrink-0'

/** Drag-to-resize wiring for the rail's right edge; absent when not resizable. */
type SidebarResize = {
  width: number
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void
  /** Keyboard resizing, in pixels per key press. */
  onNudge: (delta: number) => void
  onReset: () => void
}

const SidebarBody = ({
  onNavigate,
  isCollapsed = false,
  onToggleCollapse,
  resize,
}: {
  onNavigate?: () => void
  isCollapsed?: boolean
  onToggleCollapse?: () => void
  resize?: SidebarResize
}) => {
  const [auth] = useAuth()
  const { items, resourceLinks, settingsHref } = useMainNavItems()
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  useOnClickOutside(userMenuRef, () => setIsUserMenuOpen(false))

  const { userInitials, userFullName } = useMemo(() => {
    const user = auth.user
    const email = typeof user.email === 'string' ? user.email : ''
    const emailLocal = email ? email.split('@')[0] : ''
    return {
      userFullName:
        user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : emailLocal || email,
      userInitials:
        user.first_name && user.last_name
          ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`
          : (email || '').charAt(0).toUpperCase(),
    }
  }, [auth.user])

  const primaryItems = items.filter(
    (item) => !item.isHidden && item.id !== 'tools'
  )

  /** Wraps a collapsed row so its label is still reachable. */
  const withLabel = (label: string, node: ReactNode) =>
    isCollapsed ? (
      <Tooltip
        inline
        label={label}
        placement="right-arrow"
        maxWidth={220}
        wrapperClassName="flex w-full justify-center"
        className="flex w-full justify-center"
      >
        {node}
      </Tooltip>
    ) : (
      node
    )

  const rowShape = cn(
    rowClass,
    isCollapsed && 'size-10 w-10 shrink-0 justify-center gap-0 px-0'
  )
  // Collapsed rows are icon-only, but the label must stay in the a11y tree so
  // screen readers still announce each destination.
  const labelClass = isCollapsed
    ? 'sr-only'
    : 'min-w-0 flex-1 truncate text-left'
  const organizationName = useOrganizationName()
  const quickResources = resourceLinks.filter(
    (l) => l.id != null && RESOURCE_ICONS[l.id] != null
  )
  // The pinned rows above already cover these, so the overflow menu only lists
  // what is not already visible.
  const moreResources = resourceLinks.filter(
    (l) => l.id == null || RESOURCE_ICONS[l.id] == null
  )

  const toggleButton = onToggleCollapse ? (
    <Tooltip
      inline
      placement={isCollapsed ? 'right-arrow' : 'bottom-arrow'}
      maxWidth={240}
      label={
        <span className="flex items-center gap-2 whitespace-nowrap">
          {isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          <kbd className="rounded border border-neutral-05/25 px-1 py-px font-sans text-[10px] font-semibold tracking-wide text-neutral-05/80">
            {SIDEBAR_TOGGLE_SHORTCUT_LABEL}
          </kbd>
        </span>
      }
    >
      <button
        type="button"
        data-cy="sidebar-collapse"
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-keyshortcuts={SIDEBAR_TOGGLE_ARIA_SHORTCUT}
        aria-expanded={!isCollapsed}
        onClick={onToggleCollapse}
        className="flex size-7 shrink-0 items-center justify-center rounded-lg text-neutral-50 transition-colors hover:bg-neutral-20/70 hover:text-neutral-80"
      >
        <PanelLeftIcon className="size-[18px]" />
      </button>
    </Tooltip>
  ) : null

  return (
    <div
      className={cn(
        'relative flex h-full min-h-0 flex-col gap-4 py-4',
        isCollapsed ? 'px-3.5' : 'px-3'
      )}
    >
      {/* Brand + system status */}
      <div className="shrink-0 space-y-3">
        {isCollapsed ? (
          <div className="flex flex-col items-center gap-2">
            {toggleButton}
            {/* Collapsed, service status badges the logo instead of taking a
                row of its own: a 68px rail has no width for a status control
                that reads as a peer of the nav icons, and the icon column is
                left to navigation alone. */}
            <div className="relative">
              <Link to="/" onClick={onNavigate} className="flex items-center">
                <img
                  src="/images/XS-dark-Paths.svg"
                  alt="Inter.link"
                  className="h-12 w-auto"
                />
              </Link>
              <Tooltip
                inline
                label="Service status"
                placement="right-arrow"
                maxWidth={220}
                wrapperClassName="absolute bottom-0 right-0 z-40"
              >
                <StatusIndicator
                  variant="sidebar"
                  withSupport
                  showLabel={false}
                />
              </Tooltip>
            </div>
          </div>
        ) : (
          /* Brand card: the rail's own dark header, drawn in CSS rather than
             baked into a bitmap, so it spans the full row width and stays
             sharp on any display. */
          <div className="flex h-auto w-full items-center">
            <Link
              to="/"
              onClick={onNavigate}
              className="flex min-w-0 items-center rounded-xl w-full"
            >
              <img
                src="/images/XL-dark-Paths.svg"
                alt="Inter.link"
                className="w-full"
              />
            </Link>
          </div>
        )}

        {!isCollapsed && toggleButton && (
          <div className="absolute right-2 top-2 z-40">{toggleButton}</div>
        )}

        {/* The whole right border is the grab target: dragging it resizes the
            rail, double-click or Home restores the default width, and the arrow
            keys resize it without a pointer. */}
        {resize && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
            aria-valuenow={resize.width}
            aria-valuemin={SIDEBAR_MIN_WIDTH}
            aria-valuemax={SIDEBAR_MAX_WIDTH}
            tabIndex={0}
            data-cy="sidebar-resize"
            onPointerDown={resize.onPointerDown}
            onDoubleClick={resize.onReset}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft') {
                event.preventDefault()
                resize.onNudge(-16)
              } else if (event.key === 'ArrowRight') {
                event.preventDefault()
                resize.onNudge(16)
              } else if (event.key === 'Home') {
                event.preventDefault()
                resize.onReset()
              }
            }}
            className="group absolute -right-1.5 top-0 z-30 h-full w-3 cursor-col-resize outline-none"
          >
            <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-transparent transition-colors group-hover:bg-accent-60 group-focus-visible:bg-accent-60" />
          </div>
        )}

        {!isCollapsed && (
          <StatusIndicator
            variant="sidebar"
            withSupport
            className="[&>button]:text-[13px]"
          />
        )}
      </div>

      {/* Primary destinations */}
      <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto scrollbar-hidden">
        {primaryItems.map((item) => {
          const Icon = NAV_ICONS[item.id]
          const link = (
            <NavLink
              key={item.id}
              to={item.to ?? '#'}
              data-cy={item.id}
              target={item.external ? '_blank' : undefined}
              onClick={onNavigate}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  rowShape,
                  isActive || item.isActive ? rowActiveClass : rowIdleClass
                )
              }
            >
              {Icon && <Icon className={iconClass} />}
              <span className={labelClass}>{item.title}</span>
              {item.external && !isCollapsed && (
                <ArrowTopRightIcon className="size-3.5 shrink-0 opacity-45" />
              )}
            </NavLink>
          )
          return <div key={item.id}>{withLabel(item.title ?? '', link)}</div>
        })}

        <div className="pt-5">
          {isCollapsed ? (
            <div className="mx-2 mb-2 border-t border-hairline" />
          ) : (
            <p className="px-2.5 pb-1.5 text-[11px] font-semibold tracking-wide text-neutral-50">
              Resources
            </p>
          )}
          {quickResources.map((link) => {
            const Icon = link.id ? RESOURCE_ICONS[link.id] : undefined
            const anchor = (
              <a
                key={link.id}
                href={link.to}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(rowShape, rowIdleClass)}
              >
                {Icon && <Icon className={iconClass} />}
                <span className={labelClass}>{link.label}</span>
                {!isCollapsed && (
                  <ArrowTopRightIcon className="size-3.5 shrink-0 opacity-45" />
                )}
              </a>
            )
            return (
              <div key={link.id}>{withLabel(String(link.label), anchor)}</div>
            )
          })}
          <DropdownMenu
            id="resources-more"
            links={moreResources}
            width={240}
            alignment={isCollapsed ? 'left' : 'right'}
            className={cn(
              'flex items-center',
              isCollapsed && 'w-full justify-center'
            )}
          >
            <span className={cn(rowShape, rowIdleClass, 'cursor-pointer')}>
              <span className={iconClass}>
                {isCollapsed && <EllipsisIcon className="size-full" />}
              </span>
              <span className={labelClass}>More&hellip;</span>
            </span>
          </DropdownMenu>
        </div>
      </nav>

      {/* Account */}
      <div className="shrink-0 space-y-0.5 border-t border-hairline pt-3">
        {withLabel(
          organizationName,
          <OrganizationButton
            className={cn(rowShape, rowIdleClass, 'cursor-pointer')}
            activeClassName="bg-neutral-20/70"
          >
            {(name) => (
              <>
                <BuildingIcon className={iconClass} />
                <span className={labelClass}>{name}</span>
              </>
            )}
          </OrganizationButton>
        )}

        {withLabel(
          'Settings',
          <NavLink
            data-cy="settings-menu-button"
            to={settingsHref}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(rowShape, isActive ? rowActiveClass : rowIdleClass)
            }
          >
            <CogIcon className={iconClass} />
            <span className={labelClass}>Settings</span>
          </NavLink>
        )}

        <div
          className={cn('relative', isCollapsed && 'flex justify-center')}
          ref={userMenuRef}
        >
          <button
            type="button"
            data-cy="user-menu-button"
            onClick={() => setIsUserMenuOpen((open) => !open)}
            className={cn(rowShape, rowIdleClass, 'cursor-pointer')}
          >
            <UserAvatar initials={userInitials} />
            <span className={labelClass}>{userFullName}</span>
            {!isCollapsed && (
              <ChevronUpDownIcon className="size-4 shrink-0 text-neutral-50" />
            )}
          </button>

          {isUserMenuOpen && (
            <AccountMenu
              showIdentity={false}
              onNavigate={() => setIsUserMenuOpen(false)}
              className="absolute bottom-full left-0 z-60 mb-2 w-[248px]"
            />
          )}
        </div>
      </div>
    </div>
  )
}

const Sidebar = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const { isCollapsed, toggleCollapsed } = useSidebarCollapsed()
  useSidebarToggleShortcut(toggleCollapsed)
  const { width, setWidth, resetWidth } = useSidebarWidth()
  // The drag is tracked locally so the stored preference is written once, on
  // release, rather than on every pointer move.
  const [dragWidth, setDragWidth] = useState<number | null>(null)
  const railWidth = dragWidth ?? width

  const startResize = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return
      event.preventDefault()

      const startX = event.clientX
      const startWidth = width
      let latest = startWidth

      const onMove = (move: globalThis.PointerEvent) => {
        latest = clampSidebarWidth(startWidth + move.clientX - startX)
        setDragWidth(latest)
      }
      const onStop = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onStop)
        window.removeEventListener('pointercancel', onStop)
        document.body.classList.remove('cursor-col-resize', 'select-none')
        setDragWidth(null)
        setWidth(latest)
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onStop)
      window.addEventListener('pointercancel', onStop)
      // Keep the resize cursor and kill text selection for the whole drag,
      // even once the pointer leaves the thin handle.
      document.body.classList.add('cursor-col-resize', 'select-none')
    },
    [setWidth, width]
  )

  const nudgeWidth = useCallback(
    (delta: number) => setWidth(width + delta),
    [setWidth, width]
  )

  return (
    <>
      {/* Desktop rail */}
      <aside
        data-collapsed={isCollapsed}
        style={isCollapsed ? undefined : { width: railWidth }}
        className={cn(
          'sticky top-0 z-30 hidden h-screen shrink-0 border-r border-hairline-strong md:block',
          isCollapsed && 'w-[68px]',
          // Animating width would lag a pointer drag by a frame.
          dragWidth == null && 'transition-[width] duration-200'
        )}
      >
        <SidebarBody
          isCollapsed={isCollapsed}
          onToggleCollapse={toggleCollapsed}
          resize={
            isCollapsed
              ? undefined
              : {
                  width: railWidth,
                  onPointerDown: startResize,
                  onNudge: nudgeWidth,
                  onReset: resetWidth,
                }
          }
        />
      </aside>

      {/* Mobile bar + drawer */}
      <div className="sticky top-0 z-40 flex h-14 w-full items-center gap-2 border-b border-hairline bg-canvas px-3 md:hidden">
        <button
          type="button"
          aria-label="Open navigation"
          onClick={() => setIsDrawerOpen(true)}
          className="flex size-9 items-center justify-center rounded-lg text-neutral-70 hover:bg-neutral-20/70"
        >
          <MenuIcon className="size-5" />
        </button>
        <Link to="/" className="flex items-center">
          <img
            src="/images/logo-wide-dark.svg"
            alt="Inter.link"
            className="h-[20px] w-auto"
          />
        </Link>
      </div>

      <NavDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        label="Main navigation"
      >
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setIsDrawerOpen(false)}
          className="absolute right-2 top-3 flex size-8 items-center justify-center rounded-lg text-neutral-60 hover:bg-neutral-20/70"
        >
          <XMarkIcon className="size-5" />
        </button>
        <SidebarBody onNavigate={() => setIsDrawerOpen(false)} />
      </NavDrawer>
    </>
  )
}

export default memo(Sidebar)
